import type {
  CompsTableCommissionType,
  CompsTableCoverageStatus,
  CompsTableHeapedAmount,
  CompsTableProposalStatus,
  CompsTableSubsidyRowKind,
} from "@platform/api";
import type { ColDef } from "ag-grid-community";

/**
 * Discriminates the role of a row in the comps grid.
 *
 * - `commission` — coverage name + combined commission display per column (e.g.
 *   "Heaped 20% year 1", "Flat 14%", "Carrier Standard")
 * - `notes`      — free-text notes per column, pinned to the grid bottom
 */
export type CompsRowType = "commission" | "notes" | "subsidy";

/** Raw amount stored in a carrier cell: number for percentage/graded/pepm, heaped object for heaped. */
export type RawCommissionAmount = number | CompsTableHeapedAmount | null;

/**
 * Raw carrier cell data stored per carrier column in type/amount rows.
 * Stored as structured data so valueFormatter can produce display strings
 * while valueSetter and onCellValueChanged work with the raw values.
 */
export interface CarrierCommissionCell {
  commissionType: CompsTableCommissionType | null;
  amount: RawCommissionAmount;
  proposalStatus: CompsTableProposalStatus | undefined;
  coverageStatus: CompsTableCoverageStatus | null | undefined;
}

/** Value type for any cell in the comps grid. */
export type CompsGridCellValue =
  | string
  | CarrierCommissionCell
  | null
  | undefined;

/**
 * A single row in the comps grid.
 *
 * `rowType` and `coverageLabel` are fixed fields.
 * All other string keys are dynamic column keys holding display strings:
 * - `REQUESTED_COL_KEY` — the always-present Requested Commission column
 * - carrier UUIDs — one per carrier column
 */
export interface CompsGridRow {
  /** Stable AG Grid row id for this rendered row (e.g. `<planGroupId>:type`). */
  rowId: string;
  /** Stable coverage-level grouping key for this row (the coverage `planGroupId`). */
  rowGroupId: string;
  /** The coverage's `planGroupId`. Set on the commission row only; absent on subsidy/notes rows. */
  planGroupId?: string;
  rowType: CompsRowType;
  subsidyKind?: CompsTableSubsidyRowKind;
  coverageLabel: string;
  [columnKey: string]: CompsGridCellValue;
}

export type CompsColDef = ColDef<CompsGridRow>;

/** Field name for the pinned-left coverage name column. */
export const COVERAGE_LABEL_FIELD = "coverageLabel" as const;

/** Column key for the always-present Requested Commission column. */
export const REQUESTED_COL_KEY = "requested" as const;

/** Column key for the optional Inforce Commission column. */
export const INCUMBENT_COL_KEY = "incumbent" as const;

/**
 * Keys needed to materialize an analyst-sourced proposal plan for an unquoted
 * carrier cell (ADR-024). Sent to `POST /rfps/{id}/proposals` on the first edit
 * of a cell that has no carrier-sourced commission data yet.
 */
export interface CompsMaterializeTarget {
  rfpBenefitPlanGroupId: string;
  planPosition: number;
}

/**
 * AG Grid context passed to the Comps grid for use in mutation handlers.
 *
 * `proposalIdByCarrierId` — carrierId → proposal UUID for the carrier's active-version
 *   proposal; used for carrier-level (notes/subsidy) mutations
 * `proposalIdByCellKey` — `${columnKey}:${coverageKey}` → proposal UUID for the specific
 *   proposal a cell's commission came from (carrier columns, and the incumbent column
 *   where proposalId varies per cell because each coverage may have a different incumbent carrier)
 * `proposalPlanIdByCellKey` — `${columnKey}:${coverageKey}` → proposal plan UUID
 * `materializeTargetByRowGroupId` — coverage `rowGroupId` → materialize key for authoring
 *   an analyst proposal plan when an analyst edits an unquoted carrier cell (ADR-024)
 * `freeTextFieldByRowId` — rowId → BTX payload field name (for free-text row mutations)
 */
export interface CompsGridContext {
  proposalIdByCarrierId: Map<string, string>;
  proposalIdByCellKey: Map<string, string>;
  proposalPlanIdByCellKey: Map<string, string>;
  materializeTargetByRowGroupId: Map<string, CompsMaterializeTarget>;
  freeTextFieldByRowId: Map<string, string>;
}
